import { useForm, SubmitHandler, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormElement } from '@/components/FormElement';
import { useAutoAnimateConditional } from '@/hooks/useAutoAnimateConditional';
import { Loading } from '../Loading';
import { FormattedMessage, useIntl } from 'react-intl';
import { InputText } from '../Inputs/InputText';
import { InputPostalCode } from '../Inputs/InputPostalCode';
import { toast } from '@/hooks/useToast';
import { useQueryClient } from '@tanstack/react-query';
import { useCustomErrorMap } from '@/hooks/useCustomErrorMap';
import { FormSubmitActions } from '../FormSubmitActions';
import { useCallback, useState, useEffect } from 'react';
import { FormSection } from '../FormSection';
import { z } from 'zod';
import {
  getGetCurrentDepartmentTreeQueryKey,
  useCreateDepartment,
  useGetDepartmentByID,
  useCreatePayer,
  useGetPayerForDepartment,
} from '@/api/trafikportalen';
import { createDepartmentBody } from '@/api/trafikportalen/trafikportalenAPI.zod';
import { trimData } from '@/helpers/trimData';
import { refreshDepartmentAccess } from '@/stores/auth';
import CheckBox from '../core/Checkbox';
import { hasDepartmentWriteAccess } from '@/helpers/departmentPermissions';

const departmentFormSchema = z
  .object({
    ...createDepartmentBody.shape,
    create_payer: z.boolean().optional(),
    payer_address: z
      .object({
        street: z.string().optional(),
        zip_code: z.string().optional(),
        city: z.string().optional(),
      })
      .optional(),
    payer_reference: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (!data.create_payer) return;

    if (!data.payer_address?.street) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Adress är obligatorisk',
        path: ['payer_address', 'street'],
      });
    }
    if (!data.payer_address?.zip_code) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Postnummer är obligatoriskt',
        path: ['payer_address', 'zip_code'],
      });
    }
    if (!data.payer_address?.city) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Ort är obligatorisk',
        path: ['payer_address', 'city'],
      });
    }
    if (!data.payer_reference) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Referens är obligatorisk',
        path: ['payer_reference'],
      });
    }
  });

type DepartmentType = z.infer<typeof departmentFormSchema>;

interface DepartmentCreateFormProps {
  onOpenChange: (open: boolean) => void;
  departmentId: string;
}

export const DepartmentCreateForm = ({
  onOpenChange,
  departmentId,
}: DepartmentCreateFormProps) => {
  const [ref] = useAutoAnimateConditional<HTMLFormElement>();
  const [shouldCreatePayer, setShouldCreatePayer] = useState(false);
  const { formatMessage } = useIntl();

  const { data: parentDepartment, isLoading } =
    useGetDepartmentByID(departmentId);
  const { data: parentPayer, isLoading: isLoadingPayer } =
    useGetPayerForDepartment(departmentId);
  const createDepartment = useCreateDepartment();
  const createPayer = useCreatePayer();

  const customErrorMap = useCustomErrorMap;
  const queryClient = useQueryClient();

  const methods = useForm<DepartmentType>({
    resolver: zodResolver(departmentFormSchema, {
      errorMap: customErrorMap(),
    }),
    defaultValues: {
      parent_id: departmentId,
    },
    mode: 'all',
  });

  useEffect(() => {
    if (parentDepartment) {
      methods.setValue(
        'organization_number',
        parentDepartment.organization_number,
      );
      methods.setValue(
        'delivery_address.street',
        parentDepartment.delivery_address?.street,
      );
      methods.setValue(
        'delivery_address.zip_code',
        parentDepartment.delivery_address?.zip_code,
      );
      methods.setValue(
        'delivery_address.city',
        parentDepartment.delivery_address?.city,
      );
      methods.trigger();
    }
  }, [parentDepartment, methods]);

  useEffect(() => {
    if (shouldCreatePayer && parentPayer) {
      methods.setValue('payer_address.street', parentPayer.address.street);
      methods.setValue('payer_address.zip_code', parentPayer.address.zip_code);
      methods.setValue('payer_address.city', parentPayer.address.city);
      methods.setValue('payer_reference', parentPayer.reference);
      methods.trigger();
    }
  }, [shouldCreatePayer, parentPayer, methods]);

  const cancelEdit = useCallback(() => {
    methods.reset();
    onOpenChange?.(false);
  }, [methods, onOpenChange]);

  const onSubmit: SubmitHandler<DepartmentType> = async (data) => {
    const submitData = trimData(data);

    const { payer_address, payer_reference, ...departmentData } = submitData;

    try {
      const createdDepartment = await createDepartment.mutateAsync({
        data: departmentData,
      });

      // Create payer if requested and fields are filled
      if (
        shouldCreatePayer &&
        payer_address?.street &&
        payer_address?.zip_code &&
        payer_address?.city &&
        payer_reference
      ) {
        // Check if the new payer info matches the inherited payer info
        const payerMatchesInherited =
          parentPayer &&
          payer_address.street === parentPayer.address.street &&
          payer_address.zip_code === parentPayer.address.zip_code &&
          payer_address.city === parentPayer.address.city &&
          payer_reference === parentPayer.reference;

        if (payerMatchesInherited) {
          // Don't create payer if info matches inherited - it's redundant
          toast.info(
            {
              title: formatMessage({
                defaultMessage: 'Faktureringsuppgifter ej skapade',
              }),
              description: formatMessage({
                defaultMessage:
                  'De angivna faktureringsuppgifterna är identiska med de uppgifter som redan nyttjas från ovanstående avdelning. Inga egna faktureringsuppgifter skapades.',
              }),
            },
            { autoClose: 5000 },
          );
        } else {
          await createPayer.mutateAsync({
            data: {
              address: {
                street: payer_address.street,
                zip_code: payer_address.zip_code,
                city: payer_address.city,
              },
              reference: payer_reference,
              department_id: createdDepartment.id,
            },
          });
        }
      }

      toast.success({ title: `${data.name} skapades` }, { autoClose: 3000 });
      await refreshDepartmentAccess();
      await queryClient.invalidateQueries({
        queryKey: getGetCurrentDepartmentTreeQueryKey(),
      });
      onOpenChange(false);
    } catch (error) {
      console.error('Error: ', error);
      toast.error(
        {
          title: 'Ett fel inträffade',
          description:
            'Försök spara igen senare, eller vänligen kontakta support.',
        },
        { autoClose: 3000 },
      );
    }
  };

  if (createDepartment.isPending || isLoading || isLoadingPayer) {
    return <Loading />;
  }

  return (
    <FormProvider {...methods}>
      <form
        ref={ref}
        className="flex flex-col gap-4"
        onSubmit={methods.handleSubmit(onSubmit)}
      >
        <FormSection
          title={<FormattedMessage defaultMessage="Allmän information" />}
          editMode={true}
        >
          <FormElement
            label={<FormattedMessage defaultMessage="Underavdelning till" />}
            input={parentDepartment?.name}
          />
          <FormElement
            id="name"
            label={<FormattedMessage defaultMessage="Avdelningsnamn" />}
            required
            input={
              <InputText
                name="name"
                control={methods.control}
                editMode={true}
                error={methods.formState.errors.name}
                required
              />
            }
          />
        </FormSection>
        <FormSection
          title={<FormattedMessage defaultMessage="Fakturerings­information" />}
          editMode={!shouldCreatePayer}
        >
          {!shouldCreatePayer && parentPayer && (
            <>
              <p className="text-sm text-text-700">
                <FormattedMessage
                  defaultMessage="Denna avdelning kommer att nyttja samma faktureringsuppgifter som {departmentName}, även om faktureringsuppgifterna ändras."
                  values={{ departmentName: parentPayer.department_name }}
                />
              </p>
              <FormElement
                label={<FormattedMessage defaultMessage="Adress" />}
                input={parentPayer.address.street}
              />
              <FormElement
                label={<FormattedMessage defaultMessage="Postnummer" />}
                input={parentPayer.address.zip_code}
              />
              <FormElement
                label={<FormattedMessage defaultMessage="Postort" />}
                input={parentPayer.address.city}
              />
              <FormElement
                label={<FormattedMessage defaultMessage="Referens" />}
                input={parentPayer.reference}
              />
            </>
          )}
          {hasDepartmentWriteAccess(departmentId) && (
            <div className="flex flex-col gap-4">
              <CheckBox
                id="create-payer-checkbox"
                label={formatMessage({
                  defaultMessage:
                    'Skapa egna faktureringsuppgifter för denna avdelning',
                })}
                checked={shouldCreatePayer}
                onCheckedChange={(checked) => {
                  const isChecked = checked === true;
                  setShouldCreatePayer(isChecked);
                  methods.setValue('create_payer', isChecked);
                  methods.trigger();
                }}
              />
              {shouldCreatePayer && (
                <div className="flex flex-col gap-4">
                  <FormElement
                    id="payer_address.street"
                    label={<FormattedMessage defaultMessage="Adress" />}
                    required
                    input={
                      <InputText
                        name="payer_address.street"
                        control={methods.control}
                        editMode={true}
                        error={methods.formState.errors.payer_address?.street}
                        required
                      />
                    }
                  />
                  <FormElement
                    id="payer_address.zip_code"
                    label={<FormattedMessage defaultMessage="Postnummer" />}
                    required
                    input={
                      <InputPostalCode
                        name="payer_address.zip_code"
                        control={methods.control}
                        editMode={true}
                        error={methods.formState.errors.payer_address?.zip_code}
                        required
                      />
                    }
                  />
                  <FormElement
                    id="payer_address.city"
                    label={<FormattedMessage defaultMessage="Postort" />}
                    required
                    input={
                      <InputText
                        name="payer_address.city"
                        control={methods.control}
                        editMode={true}
                        error={methods.formState.errors.payer_address?.city}
                        required
                      />
                    }
                  />
                  <FormElement
                    id="payer_reference"
                    label={<FormattedMessage defaultMessage="Er referens" />}
                    required
                    input={
                      <InputText
                        name="payer_reference"
                        control={methods.control}
                        editMode={true}
                        error={methods.formState.errors.payer_reference}
                        required
                      />
                    }
                  />
                </div>
              )}
            </div>
          )}
        </FormSection>
        <FormSubmitActions
          active={true}
          createMode={true}
          onCancel={cancelEdit}
        />
      </form>
    </FormProvider>
  );
};
